import { useEffect, useState } from "react";
import NumberQuestion from "../../FormInputElements/NumberQuestion";
import SelectMachineField from "./SelectMachineField";
import { useSearchParams } from "react-router-dom";
import OperationDetailsFields from "./OperationDetailsFields";

const MAX_PESTICIDE_APPLICATIONS = 5;
const EMPTY_OPERATION = {
  machine_id: "",
  hours_used: "",
  operation_date: "",
  fuel_used_litres: "",
};

function hasOperationValue(operation = {}) {
  return (
    Boolean(operation.machine_id) ||
    Number(operation.hours_used) > 0 ||
    Boolean(operation.operation_date) ||
    Number(operation.fuel_used_litres) > 0
  );
}

function getVisibleOperationCount(operations = []) {
  const lastActiveIndex = operations.reduce(
    (lastIndex, operation, index) =>
      hasOperationValue(operation) ? index : lastIndex,
    -1
  );

  return Math.min(lastActiveIndex + 1, MAX_PESTICIDE_APPLICATIONS);
}

function normalizeOperations(operations = [], visibleCount = MAX_PESTICIDE_APPLICATIONS) {
  return Array.from({ length: MAX_PESTICIDE_APPLICATIONS }, (_, index) => {
    if (index >= visibleCount) {
      return { ...EMPTY_OPERATION };
    }

    return {
      ...EMPTY_OPERATION,
      ...(operations[index] || {}),
    };
  });
}

export default function PesticidesForm({
  cropYear,
  setCropYear,
  farm,
  setFarm,
}) {
  const [searchParams] = useSearchParams();
  const [numberOfPesticide, setNumberOfPesticide] = useState(
    getVisibleOperationCount(cropYear.pesticide_applications)
  );
  const issueSection = searchParams.get("issueSection");
  const issueField = searchParams.get("issueField");
  const issueRow = Number.parseInt(searchParams.get("issueRow") || "-1", 10);
  const isPesticideIssue =
    issueSection === "pesticides" ||
    (searchParams.get("issuePath") || "").startsWith(
      "pesticide_applications["
    );

  useEffect(() => {
    if (!isPesticideIssue) {
      return;
    }

    const target = document.getElementById(
      `pesticide-row-${Number.isNaN(issueRow) ? 0 : issueRow}`
    );
    const section = document.getElementById("cropyear-pesticides-section");

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isPesticideIssue, issueRow]);

  useEffect(() => {
    setNumberOfPesticide(
      getVisibleOperationCount(cropYear.pesticide_applications)
    );
  }, [cropYear.id]);

  function handleNumberPesticideChange(amount) {
    const nextAmount = Math.max(
      0,
      Math.min(MAX_PESTICIDE_APPLICATIONS, Number(amount) || 0)
    );

    setNumberOfPesticide(nextAmount);
    setCropYear((prevState) => ({
      ...prevState,
      pesticide_applications: normalizeOperations(
        prevState.pesticide_applications,
        nextAmount
      ),
    }));
  }

  function handleMachineChange(machineId, index) {
    setCropYear((prevState) => {
      const nextApplications = normalizeOperations(
        prevState.pesticide_applications
      );
      nextApplications[index] = {
        ...nextApplications[index],
        machine_id: machineId,
      };

      return {
        ...prevState,
        pesticide_applications: nextApplications,
      };
    });
  }

  function handleHoursChange(hoursUsed, index) {
    setCropYear((prevState) => {
      const nextApplications = normalizeOperations(
        prevState.pesticide_applications
      );
      nextApplications[index] = {
        ...nextApplications[index],
        hours_used: hoursUsed,
      };

      return {
        ...prevState,
        pesticide_applications: nextApplications,
      };
    });
  }

  function handleOperationDetailChange(field, value, index) {
    setCropYear((prevState) => {
      const nextApplications = normalizeOperations(prevState.pesticide_applications);
      nextApplications[index] = { ...nextApplications[index], [field]: value };
      return { ...prevState, pesticide_applications: nextApplications };
    });
  }

  return (
    <section id="cropyear-pesticides-section" className="mt-5 pb-5">
      <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
        Crop protection
      </h4>

      <NumberQuestion
        min={0}
        max={MAX_PESTICIDE_APPLICATIONS}
        fieldValue={numberOfPesticide}
        questionText={
          "How many crop-protection passes were completed this crop year?"
        }
        modalTitle={"Number of Pesticide Operations"}
        modalDescription={
          "Please provide the number of pesticide operations you had in this year for this specific field."
        }
        onChange={handleNumberPesticideChange}
      />

      <ul className="transition-all duration-500">
        {normalizeOperations(cropYear.pesticide_applications)
          .slice(0, numberOfPesticide)
          .map((pesticide, index) => (
            <li
              className="border-b border-slate-100 py-2 last:border-b-0"
              id={`pesticide-row-${index}`}
              key={index}
            >
              <SelectMachineField
              fieldLabel={"Application #" + (index + 1) + " equipment"}
              machineType={"SPRAYER"}
              index={index}
              fieldState={pesticide}
              farm={farm}
              setFarm={setFarm}
              handleHoursChange={(hoursUsed) =>
                handleHoursChange(hoursUsed, index)
              }
              handleMachineChange={(machineId) =>
                handleMachineChange(machineId, index)
              }
              errorFound={{
                machineObj:
                  isPesticideIssue &&
                  issueRow === index &&
                  (!issueField || issueField === "machine_id"),
                hoursUsed:
                  isPesticideIssue &&
                  issueRow === index &&
                  (!issueField || issueField === "hours_used"),
              }}
              />
              <OperationDetailsFields
                cropYear={cropYear.crop_year}
                operation={pesticide}
                onChange={(field, value) =>
                  handleOperationDetailChange(field, value, index)
                }
                dateLabel={`Application #${index + 1} Date`}
              />
            </li>
          ))}
      </ul>
    </section>
  );
}
